import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Icon, StatusBadge } from '../../components';
import { requestIntelligenceSurface, useIntelligenceDraft, type IntelligenceAttentionAction } from './components';
import type { SharedJourneyPorts } from './ports';
import type { IntelligenceState } from './types';
import './question-first-home.css';

export interface HomeWorkItem { id: string; title: string; detail: string; actionLabel: string; onOpen(): void; }
export interface HomeUpcomingItem {
  id: string;
  title: string;
  dateLabel: string;
  urgencyLabel?: string;
  urgencyTone?: 'neutral' | 'info' | 'warning' | 'danger';
  detail?: string;
  onOpen?(): void;
}
interface QuestionFirstHomeProps {
  contextLabel: string;
  conversationScope: string;
  documentLabel: string;
  intelligence: IntelligenceState;
  ports: Pick<SharedJourneyPorts, 'loadHomeSuggestions'>;
  work: readonly HomeWorkItem[];
  upcoming?: readonly HomeUpcomingItem[];
  attention: readonly IntelligenceAttentionAction[];
  onOpenDocuments(): void;
  onOpenIntelligence(): void;
}

/** A read-only projection of existing server work. No tax/eligibility calculations or automatic actions. */
export function QuestionFirstHome({ contextLabel, conversationScope, documentLabel, intelligence, ports,
  work, upcoming = [], attention, onOpenDocuments, onOpenIntelligence }: QuestionFirstHomeProps) {
  const [prompt, setPrompt] = useIntelligenceDraft(conversationScope);
  const [suggestions, setSuggestions] = useState<{ hints: string[]; tasks: Array<{ label: string; prompt: string }> }>({ hints: [], tasks: [] });
  const [hintIndex, setHintIndex] = useState(0);
  const [focused, setFocused] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const loader = useRef(ports.loadHomeSuggestions); loader.current = ports.loadHomeSuggestions;
  useEffect(() => {
    const controller = new AbortController();
    setSuggestions({ hints: [], tasks: [] }); setHintIndex(0);
    void Promise.resolve().then(() => loader.current?.(controller.signal)).then(value => {
      if (!controller.signal.aborted && value) setSuggestions(value);
    }).catch(() => { /* Suggestions are optional; the scoped question path remains available. */ });
    return () => controller.abort();
  }, [conversationScope]);
  useEffect(() => {
    if (focused || prompt || suggestions.hints.length < 2) return;
    const media = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    let timer: ReturnType<typeof setInterval> | undefined;
    const update = () => {
      if (timer) clearInterval(timer);
      if (!media?.matches) timer = setInterval(() => setHintIndex(index => (index + 1) % suggestions.hints.length), 3200);
    };
    update(); media?.addEventListener('change', update);
    return () => { if (timer) clearInterval(timer); media?.removeEventListener('change', update); };
  }, [focused, prompt, suggestions.hints.length]);

  const ask = (event: FormEvent) => {
    event.preventDefault();
    const text = prompt.trim();
    if (!text || intelligence.status === 'unavailable') return;
    requestIntelligenceSurface(conversationScope, 'ask', { submit: true });
    onOpenIntelligence();
  };
  return <section className="am-question-home" aria-labelledby="am-home-title">
    <header><p>{contextLabel}</p><h1 id="am-home-title">What do you need to do?</h1></header>
    <form className="am-question-home__ask" onSubmit={ask}>
      <Icon name="search" size={20} />
      <input ref={input} aria-label="Ask Amoye what you need to do" value={prompt} maxLength={4000}
        onChange={event => setPrompt(event.target.value)} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
        placeholder={suggestions.hints[hintIndex] ?? 'Ask about your tax work'} disabled={intelligence.status === 'unavailable'} />
      <button className="am-question-home__add" type="button" onClick={onOpenDocuments} aria-label={documentLabel} title={documentLabel}><span aria-hidden="true">+</span></button>
      <button className="am-question-home__send" type="submit"
        disabled={!prompt.trim() || intelligence.status === 'unavailable'} aria-label="Go to Intelligence and ask">
        <Icon name="arrow-up" size={18} />
      </button>
    </form>
    {suggestions.tasks.length > 0 && <div className="am-question-home__suggestions" aria-label="Suggested questions">{suggestions.tasks.map(task => <button key={task.prompt} type="button"
      onClick={() => { setPrompt(task.prompt); input.current?.focus(); }}>{task.label}</button>)}</div>}
    <p className="am-question-home__disclaimer">Amoye Intelligence explains and suggests next steps. It cannot change figures, approve, sign, file or pay.</p>
    {intelligence.status === 'unavailable' && <p role="status">Your existing work remains available below while Amoye Intelligence reconnects.</p>}
    <div className="am-question-home__columns"><section aria-labelledby="am-home-continue"><h2 id="am-home-continue">Continue where you left off</h2>
      {work.length ? <ul>{work.map(item => <li key={item.id}><button type="button" onClick={item.onOpen} aria-label={item.actionLabel}><span><strong>{item.title}</strong><small>{item.detail}</small></span><span aria-hidden="true">→</span></button></li>)}</ul>
        : <p>No work is waiting here. Add your {documentLabel.toLowerCase().includes('document') ? 'documents' : 'records'} to get started.</p>}</section>
      <div><section className="am-question-home__upcoming" aria-labelledby="am-home-upcoming"><h2 id="am-home-upcoming">Coming up</h2>{upcoming.length ? <ul>{upcoming.map(item => <li key={item.id}>{item.onOpen ? <button type="button" onClick={item.onOpen}><span><strong>{item.dateLabel}</strong><small>{item.title}{item.detail ? ` · ${item.detail}` : ''}</small></span>{item.urgencyLabel && <StatusBadge tone={item.urgencyTone}>{item.urgencyLabel}</StatusBadge>}</button>
        : <><span><strong>{item.dateLabel}</strong><small>{item.title}{item.detail ? ` · ${item.detail}` : ''}</small></span>{item.urgencyLabel && <StatusBadge tone={item.urgencyTone}>{item.urgencyLabel}</StatusBadge>}</>}</li>)}</ul>
        : <p>No confirmed deadlines are available in this view. Check your return and filing details.</p>}</section>
      <section aria-labelledby="am-home-attention"><h2 id="am-home-attention">Attention</h2>{attention.length ? <ul>{attention.map(item => <li key={item.id}><button type="button" onClick={item.onOpen}><span><strong>{item.title}</strong><small>{item.detail}</small></span><span aria-hidden="true">→</span></button></li>)}</ul>
        : <p>No open attention items in the available records.</p>}</section></div></div>
  </section>;
}
